if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        this.variantImageSetsEnabled = this.dataset.variantImageSets === 'true';
        this.imageSetName = null;
        this.imageSetIndex = null;
        this.currentImageSet = null;
        this.variantChangeUnsubscriber = undefined;
        this.optionValueSelectionChangeUnsubscriber = undefined;
        this.preloadedImages = [];
        this.mediaPreloadQueued = false;

        this.setupImmediateVariantMedia();
        if (this.variantImageSetsEnabled) this.setupVariantImageSets();
        this.queueMediaPreload();

        if (!this.elements.thumbnails) return;

        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
      }

      disconnectedCallback() {
        this.variantChangeUnsubscriber?.();
        this.optionValueSelectionChangeUnsubscriber?.();
      }

      setupVariantImageSets() {
        const imageSetElement = this.querySelector('[data-set-name]');
        if (!imageSetElement) return;

        this.imageSetName = imageSetElement.dataset.setName;
        this.imageSetIndex = this.getImageSetOptionIndex();
        if (this.imageSetIndex === null) return;

        this.filterVariantImageSet(this.getCurrentVariant());

        if (typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return;
        this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, ({ data }) => {
          if (data.sectionId !== this.dataset.section) return;
          this.filterVariantImageSet(data.variant);
        });
      }

      setupImmediateVariantMedia() {
        if (typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return;

        this.optionValueSelectionChangeUnsubscriber = subscribe(
          PUB_SUB_EVENTS.optionValueSelectionChange,
          this.handleOptionValueSelectionChange.bind(this)
        );
      }

      handleOptionValueSelectionChange({ data }) {
        const productInfo = this.closest('product-info');
        if (!productInfo?.contains(data.event.target)) return;

        if (this.variantImageSetsEnabled) {
          const optionElement = data.event.target.closest('.product-form__input[data-option-handle]');
          if (optionElement?.dataset.optionHandle === this.imageSetName) {
            this.filterImageSetGroup(`${this.imageSetName}_${this.handleize(data.event.target.value)}`);
          }
          return;
        }

        const variant = this.getSelectedVariantFromOptions();
        if (!variant?.featured_media?.id) return;

        this.setActiveMedia(`${this.dataset.section}-${variant.featured_media.id}`, true);
      }

      getImageSetOptionIndex() {
        const productInfo = this.closest('product-info') || document;
        const optionElement = Array.from(productInfo.querySelectorAll('.product-form__input[data-option-handle]')).find(
          (element) => element.dataset.optionHandle === this.imageSetName
        );
        if (!optionElement?.dataset.optionPosition) return null;

        const index = Number(optionElement.dataset.optionPosition) - 1;
        return Number.isFinite(index) && index >= 0 ? index : null;
      }

      getCurrentVariant() {
        const selectedVariant = this.closest('product-info')?.querySelector('variant-selects [data-selected-variant]');
        if (!selectedVariant?.textContent) return null;

        try {
          return JSON.parse(selectedVariant.textContent);
        } catch (error) {
          return null;
        }
      }

      getSelectedVariantFromOptions() {
        const productInfo = this.closest('product-info');
        const variantSelects = productInfo?.querySelector('variant-selects');
        const variantsJson = variantSelects?.querySelector('[data-product-variants]');
        if (!variantSelects || !variantsJson?.textContent) return null;

        const selectedOptions = Array.from(
          variantSelects.querySelectorAll('.product-form__input[data-option-position]')
        )
          .sort((a, b) => Number(a.dataset.optionPosition) - Number(b.dataset.optionPosition))
          .map((optionElement) => optionElement.querySelector('select')?.value || optionElement.querySelector('input:checked')?.value);

        if (!selectedOptions.length || selectedOptions.some((value) => !value)) return null;

        try {
          const variants = JSON.parse(variantsJson.textContent);
          return variants.find((variant) =>
            selectedOptions.every((optionValue, index) => variant.options?.[index] === optionValue)
          );
        } catch (error) {
          return null;
        }
      }

      getImageSetGroup(variant) {
        if (!variant || this.imageSetIndex === null || !this.imageSetName) return null;

        const optionValue = variant.options?.[this.imageSetIndex] || variant[`option${this.imageSetIndex + 1}`];
        if (!optionValue) return null;

        return `${this.imageSetName}_${this.handleize(optionValue)}`;
      }

      handleize(value) {
        return String(value)
          .toLowerCase()
          .replace(/[^a-z0-9]+/g, '-')
          .replace(/^-+|-+$/g, '');
      }

      filterVariantImageSet(variant) {
        const group = this.getImageSetGroup(variant);
        this.filterImageSetGroup(group);
      }

      filterImageSetGroup(group) {
        if (!group || group === this.currentImageSet) return;

        this.currentImageSet = group;

        const slides = Array.from(this.elements.viewer.querySelectorAll('li[data-media-id]'));
        const visibleSlides = this.toggleImageSetItems(slides, group);

        if (this.elements.thumbnails) {
          this.toggleImageSetItems(Array.from(this.elements.thumbnails.querySelectorAll('[data-target]')), group);
        }

        this.toggleImageSetItems(Array.from(this.querySelectorAll('[data-madeleine-gallery-dots] [data-target]')), group);
        this.refreshSliders();

        const firstVisibleSlide = visibleSlides[0];
        if (firstVisibleSlide) this.setActiveMedia(firstVisibleSlide.dataset.mediaId, false);
      }

      toggleImageSetItems(items, group) {
        const visibleItems = [];

        items.forEach((item) => {
          const isVisible = item.dataset.group === group;
          item.classList.toggle('hidden', !isVisible);
          if (isVisible) visibleItems.push(item);
        });

        return visibleItems;
      }

      refreshSliders() {
        this.elements.viewer?.resetPages?.();
        this.elements.thumbnails?.resetPages?.();
      }

      queueMediaPreload() {
        if (this.mediaPreloadQueued) return;
        this.mediaPreloadQueued = true;

        const schedulePreload = () => {
          const preload = this.preloadMediaImages.bind(this);

          if ('requestIdleCallback' in window) {
            window.requestIdleCallback(preload, { timeout: 2500 });
          } else {
            window.setTimeout(preload, 800);
          }
        };

        if (document.readyState === 'complete') {
          schedulePreload();
        } else {
          window.addEventListener('load', schedulePreload, { once: true });
        }
      }

      preloadMediaImages() {
        const images = Array.from(
          this.elements.viewer.querySelectorAll('.product__media img, .deferred-media__poster img')
        );

        images.forEach((image) => {
          if (image.complete && image.naturalWidth > 0) return;

          image.loading = 'eager';

          const preloadImage = new Image();
          const sizes = image.getAttribute('sizes');
          const srcset = image.getAttribute('srcset');
          const src = image.currentSrc || image.getAttribute('src');

          if (sizes) preloadImage.sizes = sizes;
          if (srcset) preloadImage.srcset = srcset;
          if (src) preloadImage.src = src;

          this.preloadedImages.push(preloadImage);
        });
      }

      onSlideChanged(event) {
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) {
          return;
        }
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.firstChild !== activeThumbnail && activeThumbnail.parentElement.prepend(activeThumbnail);
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          const activeMediaRect = activeMedia.getBoundingClientRect();
          const stickyHeaderOffset = this.getStickyHeaderOffset();
          // Don't scroll if the image is already fully visible below the sticky header.
          if (activeMediaRect.top >= stickyHeaderOffset && activeMediaRect.bottom <= window.innerHeight) return;

          const top = activeMediaRect.top + window.scrollY - stickyHeaderOffset - 8;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      getStickyHeaderOffset() {
        const header = document.querySelector('.section-header:not(.shopify-section-header-hidden)');
        if (!header) return 0;

        const rect = header.getBoundingClientRect();
        return rect.bottom > 0 ? rect.bottom : 0;
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
